import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ExternalLinkIcon, LinkIcon, SmartphoneIcon } from 'lucide-react';

import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import webViewStore from '@client/stores/webViewStore';
import { useState } from 'react';

// 환경에 따른 딥링크 설정
const getEnvironmentConfig = () => {
    const hostname = window.location.hostname;

    if (
        hostname.includes('dev.') ||
        hostname.includes('localhost') ||
        hostname.includes('127.0.0.1')
    ) {
        return {
            customScheme: 'haewaodev://',
            domain: 'https://dev.haewao.com',
        };
    }

    return {
        customScheme: 'haewao://',
        domain: 'https://haewao.com',
    };
};

class ViewStore {
    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {}
}

interface DeepLinkTestCase {
    title: string;
    description: string;
    links: {
        customScheme?: string;
        universalLink?: string;
        webUrl?: string;
    };
}

const getTestCases = (): DeepLinkTestCase[] => {
    const { customScheme, domain } = getEnvironmentConfig();

    return [
        {
            title: '홈페이지',
            description: '메인 홈페이지로 이동',
            links: {
                customScheme: customScheme,
                universalLink: domain,
                webUrl: domain,
            },
        },
        {
            title: '뉴스 목록',
            description: '뉴스 목록 페이지로 이동',
            links: {
                customScheme: `${customScheme}news`,
                universalLink: `${domain}/news`,
                webUrl: `${domain}/news`,
            },
        },
        {
            title: '포트폴리오 목록',
            description: '포트폴리오 목록 페이지로 이동',
            links: {
                customScheme: `${customScheme}portfolio`,
                universalLink: `${domain}/portfolio`,
                webUrl: `${domain}/portfolio`,
            },
        },
        {
            title: '특정 뉴스 상세',
            description: '특정 뉴스 상세 페이지로 이동',
            links: {
                customScheme: `${customScheme}news/sample-news-id`,
                universalLink: `${domain}/news/sample-news-id`,
                webUrl: `${domain}/news/sample-news-id`,
            },
        },
        {
            title: '특정 포트폴리오 상세',
            description: '특정 포트폴리오 상세 페이지로 이동',
            links: {
                customScheme: `${customScheme}portfolio/sample-portfolio-id`,
                universalLink: `${domain}/portfolio/sample-portfolio-id`,
                webUrl: `${domain}/portfolio/sample-portfolio-id`,
            },
        },
    ];
};

const DeepLinkTestBox = observer(({ testCase }: { testCase: DeepLinkTestCase }) => {
    const [copiedLink, setCopiedLink] = useState<string>('');

    const copyToClipboard = async (link: string, type: string) => {
        try {
            await navigator.clipboard.writeText(link);
            setCopiedLink(`${type}: ${link}`);
            setTimeout(() => setCopiedLink(''), 3000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const openLink = (url: string) => {
        if (webViewStore.isWebView) {
            // 웹뷰 환경에서는 앱 브릿지를 통해 새 웹뷰로 열기
            webViewStore.sendMessageToApp('openWebView', { url });
        } else {
            // 웹 환경에서는 새 탭으로 열기
            window.open(url, '_blank');
        }
    };

    return (
        <Box>
            <div className="flex items-start justify-between">
                <div>
                    <Box.Title title={testCase.title} />
                    <p className="text-base-content/70 -mt-4 text-sm">{testCase.description}</p>
                </div>
            </div>

            <div className="space-y-3">
                {/* 커스텀 스킴 링크 */}
                {testCase.links.customScheme && (
                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <div className="text-primary flex items-center gap-1 text-sm font-medium">
                                <SmartphoneIcon size={16} />
                                딥링크 (커스텀 스킴)
                            </div>
                            <code className="bg-base-200 rounded px-2 py-1 text-xs">
                                {testCase.links.customScheme}
                            </code>
                        </div>
                        <button
                            className="btn btn-xs btn-outline"
                            onClick={() => copyToClipboard(testCase.links.customScheme!, '딥링크')}
                        >
                            복사
                        </button>
                        <button
                            className="btn btn-xs btn-primary"
                            onClick={() => window.open(testCase.links.customScheme, '_self')}
                        >
                            테스트
                        </button>
                    </div>
                )}

                {/* Universal Link */}
                {testCase.links.universalLink && (
                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <div className="text-secondary flex items-center gap-1 text-sm font-medium">
                                <LinkIcon size={16} />
                                Universal/App Link
                            </div>
                            <code className="bg-base-200 rounded px-2 py-1 text-xs">
                                {testCase.links.universalLink}
                            </code>
                        </div>
                        <button
                            className="btn btn-xs btn-outline"
                            onClick={() =>
                                copyToClipboard(testCase.links.universalLink!, 'Universal Link')
                            }
                        >
                            복사
                        </button>
                        <button
                            className="btn btn-xs btn-secondary"
                            onClick={() => openLink(testCase.links.universalLink!)}
                        >
                            테스트
                        </button>
                    </div>
                )}

                {/* Web URL */}
                {testCase.links.webUrl && (
                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <div className="text-accent flex items-center gap-1 text-sm font-medium">
                                <ExternalLinkIcon size={16} />웹 URL
                            </div>
                            <code className="bg-base-200 rounded px-2 py-1 text-xs">
                                {testCase.links.webUrl}
                            </code>
                        </div>
                        <button
                            className="btn btn-xs btn-outline"
                            onClick={() => copyToClipboard(testCase.links.webUrl!, 'Web URL')}
                        >
                            복사
                        </button>
                        <button
                            className="btn btn-xs btn-accent"
                            onClick={() => openLink(testCase.links.webUrl!)}
                        >
                            테스트
                        </button>
                    </div>
                )}
            </div>

            {copiedLink && (
                <div className="alert alert-success mt-2">
                    <span className="text-xs">클립보드에 복사됨: {copiedLink}</span>
                </div>
            )}
        </Box>
    );
});

const DeepLinkTestPage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    if (typeof window === 'undefined') {
        return null;
    }

    return (
        <Frame>
            <Frame.Title title="딥링크 테스트" />

            {/* 환경 정보 */}
            <Box>
                <Box.Title title="환경 정보" />
                <div className="space-y-2 text-sm">
                    <div>
                        <span className="font-medium">현재 환경:</span>{' '}
                        {webViewStore.isWebView ? '웹뷰' : '웹 브라우저'}
                    </div>
                    <div>
                        <span className="font-medium">도메인:</span> {window.location.hostname}
                    </div>
                    <div>
                        <span className="font-medium">현재 URL:</span> {window.location.href}
                    </div>
                    {webViewStore.isWebView && (
                        <div>
                            <span className="font-medium">브릿지 버전:</span>{' '}
                            {webViewStore.bridgeVersion}
                        </div>
                    )}
                </div>
            </Box>

            {/* 사용 방법 안내 */}
            <Box>
                <Box.Title title="테스트 방법" />
                <div className="space-y-2 text-sm">
                    <p>
                        <span className="font-medium">1. 복사 버튼:</span> 링크를 클립보드에
                        복사하여 다른 앱에서 테스트 가능
                    </p>
                    <p>
                        <span className="font-medium">2. 테스트 버튼:</span> 현재 환경에서 직접 링크
                        실행
                    </p>
                    <p>
                        <span className="font-medium">3. 딥링크:</span> 앱이 설치된 경우 앱으로
                        이동, 미설치시 스토어로 이동
                    </p>
                    <p>
                        <span className="font-medium">4. Universal/App Link:</span> 앱 설치시 앱으로
                        이동, 미설치시 웹으로 이동
                    </p>
                    <p>
                        <span className="font-medium">5. 웹 URL:</span> 일반 웹 링크로 동작
                    </p>
                </div>
            </Box>

            {/* 인증 파일 확인 */}
            <Box>
                <Box.Title title="인증 파일 확인" />
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">iOS Universal Links:</span>
                        <a
                            href="/apple-app-site-association"
                            target="_blank"
                            className="btn btn-xs btn-outline"
                        >
                            apple-app-site-association
                        </a>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Android App Links:</span>
                        <a
                            href="/.well-known/assetlinks.json"
                            target="_blank"
                            className="btn btn-xs btn-outline"
                        >
                            assetlinks.json
                        </a>
                    </div>
                </div>
            </Box>

            {/* 테스트 케이스 */}
            {getTestCases().map((testCase, index) => (
                <DeepLinkTestBox key={index} testCase={testCase} />
            ))}
        </Frame>
    );
});

export default DeepLinkTestPage;
